import { useCallback, useEffect, useRef, useState } from "react";
import { supabase, safeChannel } from "./supabase";

// useKidRequests — the parent inbox for kid food/recipe requests.
// Returns pending + done lists, mutators for status changes, and
// a generateRecipe helper that wires the request's ingredient
// list into the existing generate-recipe edge function.
//
// Reads via RLS (family-scope) on kid_food_requests. Realtime is
// already on the table from 0183 so new requests pop into the
// list within a second of the kid hitting "I WANT THIS!".

export function useKidRequests(userId) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const chRef = useRef(null);

  const load = useCallback(async () => {
    if (!userId) {
      setRows([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data, error } = await supabase
      .from("kid_food_requests")
      .select("id, kid_user_id, request_kind, canonical_id, recipe_slug, food_emoji, food_name, ingredients, technique_emoji, technique_name, result_image_url, status, generated_recipe_id, created_at, resolved_at, resolved_by")
      .order("created_at", { ascending: false })
      .limit(200);
    if (error) {
      console.warn("[kid-requests] load:", error.message);
      setError(error);
    }
    setRows(data || []);
    setLoading(false);
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!userId) return;
    const ch = safeChannel(`rt:kid_food_requests:${userId}`)
      .on("postgres_changes",
        { event: "*", schema: "public", table: "kid_food_requests" },
        () => load())
      .subscribe();
    chRef.current = ch;
    return () => {
      if (chRef.current) {
        supabase.removeChannel(chRef.current);
        chRef.current = null;
      }
    };
  }, [userId, load]);

  const setStatus = useCallback(async (id, status) => {
    const { error } = await supabase
      .from("kid_food_requests")
      .update({
        status,
        resolved_at: status === "pending" ? null : new Date().toISOString(),
        resolved_by: status === "pending" ? null : userId,
      })
      .eq("id", id);
    if (error) {
      console.warn("[kid-requests] setStatus:", error.message);
      throw error;
    }
  }, [userId]);

  const markDone = useCallback((id) => setStatus(id, "done"), [setStatus]);
  const dismiss  = useCallback((id) => setStatus(id, "dismissed"), [setStatus]);
  const reopen   = useCallback((id) => setStatus(id, "pending"), [setStatus]);

  // Accept = "yes, I'll make it." Flips to 'seen' so the kid's My Asks
  // strip lights up ✋ "Coming!" — that's the whole "tell her she's
  // getting it" half of the loop. NOT setStatus(): 'seen' is an
  // acknowledgement, not a resolution, so it must NOT stamp
  // resolved_at/by (that reads as fulfilled and would drop it from
  // her active strip). Realtime reload repaints both sides.
  const accept = useCallback(async (id) => {
    const { error } = await supabase
      .from("kid_food_requests")
      .update({ status: "seen" })
      .eq("id", id);
    if (error) { console.warn("[kid-requests] accept:", error.message); throw error; }
  }, []);

  // Generate a real recipe from a kid request's ingredient list.
  // Pipes through the existing generate-recipe edge function with
  // the kid's ingredients as the "pantry" + kid-friendly prefs.
  // The result is saved into user_recipes owned by the calling
  // adult and stamped on the request row for next-time discovery.
  const generateRecipe = useCallback(async (request) => {
    if (!request || !userId) throw new Error("missing request or user");
    const ingredients = Array.isArray(request.ingredients) ? request.ingredients : [];
    if (ingredients.length === 0) {
      throw new Error("this request has no ingredient list — too old to auto-generate");
    }

    // Shape the kid's ingredients into a pantry array the edge fn
    // understands. Names from the kid app are already canonical-ish
    // (Egg, Tomato, Wheat, etc.) so we let the AI resolve them.
    const pantry = ingredients.map((i) => ({
      name:   i.name || i.emoji,
      amount: 1,
      unit:   "to taste",
    }));
    const techniqueHint = request.technique_name
      ? ` The kid used the ${request.technique_name.toLowerCase()} technique.`
      : "";
    const prefs = {
      difficulty: "easy",
      time:       "30min",
      notes: `A kid combined these ingredients in a craft game and named the result "${request.food_name}".${techniqueHint}

CRITICAL: Use ONLY the listed ingredients plus basic seasonings (salt, pepper, oil, water). Do NOT add ingredients that weren't supplied (no garlic, herbs, vanilla, eggs, milk, sugar, flour, etc. unless they appear in the input list).

If the kid's name "${request.food_name}" is a real dish that actually requires extra ingredients NOT in the list (e.g. they said "Cornbread" but only have corn — cornbread needs flour, egg, milk), IGNORE the name and create the simplest real kid-friendly dish that can be made from EXACTLY the listed ingredients (e.g. corn alone → "Grilled Corn" or "Buttered Corn"). Note that mismatch in the recipe description so the parent knows.

If the name IS achievable with the listed ingredients, follow it as named.`,
    };

    const { data, error } = await supabase.functions.invoke("generate-recipe", {
      body: { pantry, prefs },
    });
    if (error) throw error;
    if (data?.error) throw new Error(data.error);
    const recipe = data?.recipe;
    if (!recipe) throw new Error("generate-recipe returned no recipe");

    // Save into user_recipes for this adult. Use the kid-suggested
    // name + emoji on the saved row so it's easy to spot in the
    // recipe book.
    const recipePayload = {
      ...recipe,
      title: recipe.title || request.food_name,
      emoji: recipe.emoji || request.food_emoji,
    };
    const slug = (recipePayload.slug || request.food_name || "kid_recipe")
      .toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60)
      || "kid-recipe";

    const { data: insertedRows, error: insErr } = await supabase
      .from("user_recipes")
      .insert({
        user_id: userId,
        slug,
        source:  "kid_request",
        recipe:  { ...recipePayload, slug },
        tags:    ["kid", "from-kid-request"],
      })
      .select("id")
      .single();
    if (insErr) throw insErr;

    // Stamp the generated recipe id back on the request + mark it
    // as done so it leaves the pending queue.
    await supabase
      .from("kid_food_requests")
      .update({
        status: "done",
        resolved_at: new Date().toISOString(),
        resolved_by: userId,
        generated_recipe_id: insertedRows.id,
      })
      .eq("id", request.id);

    return { user_recipe_id: insertedRows.id, recipe: recipePayload };
  }, [userId]);

  const pending = rows.filter((r) => r.status === "pending");
  const done    = rows.filter((r) => r.status === "done");
  const dismissed = rows.filter((r) => r.status === "dismissed");

  return {
    rows,
    pending,
    done,
    dismissed,
    loading,
    error,
    refresh: load,
    accept,
    markDone,
    dismiss,
    reopen,
    generateRecipe,
  };
}
